import { forwardRef, ReactElement, ReactNode } from "react";
import { AppBarData } from "../appBar/appBarDomain";
import { Box, VBox, VBoxProps } from "./Box";
import { BackButton, Button, CloseButton } from "./Button";
import { DropdownMenu, DropdownMenuProps } from "./Dropdown";
import { AppBar } from "./AppBar";
import { isReactElement } from "../utils/reactHelpers";
import { Transition } from "@headlessui/react";
import AlertMessageStore from "../stores/AlertMessageStore";
import { AlertContainer } from "./Alert";
import { BgColor } from "./colors";

import { useTranslation } from "../stores/RootStoreContext";
import { BoxProps } from "./boxHelpers";
import { LoadingIndicator } from "./LoadingIndicator";

export type PageHeaderProps = {
  title: ReactElement | string | number;
  menu?: DropdownMenuProps | ReactElement;
  isMaximized?: boolean;
  onToggleMaximize?: () => void;
  onGoBack?: () => void;
  onGoBackName?: string;
  onGoBackVariant?: "arrow" | "x";
  containerProps?: BoxProps;
};

export const PageHeader = forwardRef<HTMLDivElement, PageHeaderProps>(function PageHeader(
  { title, menu, isMaximized, onToggleMaximize, onGoBack, onGoBackVariant, onGoBackName },
  ref,
) {
  const { t } = useTranslation();
  return (
    <Box ref={ref} paddingX paddingY={"sm"} borderBottom bg gap={"lg"} alignItems={"center"}>
      {onGoBack &&
        (onGoBackVariant === "x" ? (
          <CloseButton onClick={onGoBack} />
        ) : (
          <BackButton onClick={onGoBack} name={onGoBackName} />
        ))}
      {title}
      <Box marginLeft={"auto"}>
        {typeof menu === "object" && "button" in menu ? <DropdownMenu {...menu} /> : menu}
      </Box>
      {onToggleMaximize && (
        <Button
          variant={"basic"}
          icon={isMaximized ? "arrows-in-simple" : "arrows-out-simple"}
          tooltip={isMaximized ? t("restore") : t("maximize")}
          onClick={onToggleMaximize}
        />
      )}
    </Box>
  );
});

export type FullScreenLayoutProps = {
  appBar?: AppBarData | ReactElement | null | false;
  alertStore?: AlertMessageStore;
  pageHeader?: PageHeaderProps | ReactElement | false;
  leftPanelContainerProps?: VBoxProps;
  leftPanel?: ReactNode;
  centerPanelContainerProps?: BoxProps;
  centerPanel?: ReactElement | "loader";
  rightPanelContainerProps?: VBoxProps;
  rightPanel?: ReactElement;
  footerPanel?: ReactElement;
  bg?: BgColor | boolean;
  disableAppBarShadow?: boolean;
  className?: string;
};

export const FullScreenLayout = forwardRef<HTMLDivElement, FullScreenLayoutProps>(
  function FullScreenLayout(
    {
      appBar,
      alertStore,
      pageHeader,
      leftPanelContainerProps,
      leftPanel,
      centerPanelContainerProps,
      centerPanel,
      rightPanelContainerProps,
      rightPanel,
      footerPanel,
      bg = "base",
      disableAppBarShadow,
      className,
    },
    ref,
  ) {
    const hasPageHeader = Boolean(pageHeader);
    const hasLeftPanel = Boolean(leftPanel);
    const hasRightPanel = Boolean(rightPanel);
    const isMaximized = pageHeader && !isReactElement(pageHeader) && pageHeader.isMaximized;

    return (
      <VBox className={className} ref={ref} fullscreen gap={0}>
        {appBar && (
          <Transition
            show={!isMaximized}
            enter={"tw-transition"}
            enterFrom={"-tw-translate-y-full"}
            enterTo={"tw-translate-y-0"}
            leave={"tw-transition"}
            leaveFrom={"tw-translate-y-0"}
            leaveTo={"-tw-translate-y-full"}
          >
            <VBox flex={"none"} gap={0}>
              {isReactElement(appBar) ? (
                appBar
              ) : (
                <AppBar
                  data={appBar}
                  disableDropShadow={
                    disableAppBarShadow || hasPageHeader || hasLeftPanel || hasRightPanel
                  }
                />
              )}
            </VBox>
          </Transition>
        )}
        {alertStore && <AlertContainer messageStore={alertStore} />}
        {pageHeader && (
          <VBox flex={"none"} gap={0}>
            {isReactElement(pageHeader) ? pageHeader : <PageHeader {...pageHeader} />}
          </VBox>
        )}
        <Box gap={0} overflow={"hidden"} bg={bg} grow>
          {leftPanel && (
            <VBox overflowY={"auto"} borderRight bg {...leftPanelContainerProps}>
              {leftPanel}
            </VBox>
          )}
          {centerPanel && (
            <Box vertical overflowY={"auto"} gap={0} grow {...centerPanelContainerProps}>
              {centerPanel === "loader" ? (
                <VBox grow={"golden"} justifyContent="center">
                  <LoadingIndicator />
                </VBox>
              ) : (
                centerPanel
              )}
            </Box>
          )}
          {rightPanel && (
            <VBox overflowY={"auto"} borderLeft gap={0} bg {...rightPanelContainerProps}>
              {rightPanel}
            </VBox>
          )}
        </Box>
        {footerPanel && (
          <VBox flex={"none"} gap={0}>
            {footerPanel}
          </VBox>
        )}
      </VBox>
    );
  },
);
